import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Observable, Subscription, switchMap } from 'rxjs';
import {
  NavigationAppearance,
  NavigationItem,
  NavigationMode,
  NavigationType,
} from '@app/core/components/navigation/navigation.types';
import { MediaWatcherService } from '@app/shared/services/media-watcher';
import { AuthFacade } from '@app/shared/store/auth/auth.facade';
import {
  ALL_MEMBERS,
  ALL_PLANS,
  UserAccessRolesEnum,
} from '@app/shared/models/user/user-access';
import { shareObservable } from '@app/helpers/rxjs-helper';
import { filter, tap } from 'rxjs/operators';
import { DashboardService } from '@app/shared/services/dashboard.service';
import { GeoDashboard } from '@app/shared/models/geo-dashboard';
import { PlanTypeEnum } from '@app/shared/models/payment-result';
import { PlanSubscription } from '@app/shared/models/user/plan-subscription';
import {
  PaymentGateway,
  PlatformConfig,
  PlatformNameEnum,
} from '@app/configs/geokTheme';
import { PlatformConfigFacade } from '@app/shared/store/platform-config/platform-config-facade';
import { AddonsFacade } from '@app/shared/store/addons/addons-facade';
import { ICustomerAddonSerialized } from '@app/modules/addons/models/addons.model';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-geo-navigation-container',
  templateUrl: './geo-navigation-container.component.html',
  styleUrls: ['./geo-navigation-container.component.scss'],
})
export class GeoNavigationContainerComponent
  implements OnInit, OnChanges, OnDestroy
{
  navigationMode: typeof NavigationMode = NavigationMode;
  navigationAppearance: typeof NavigationAppearance = NavigationAppearance;
  navigationItems: NavigationItem[] = [];
  isScreenSmall!: boolean;
  private userRolesSubscription$: Subscription = new Subscription();
  private companySubscription$: Subscription = new Subscription();
  private mediaWatcher$: Subscription = new Subscription();
  userRoles: UserAccessRolesEnum[] = [];
  private dashboardNavItems: NavigationItem[] = [];
  currentPlan = PlanTypeEnum.ESSENTIAL;
  currentPaymentGateway: PaymentGateway = PaymentGateway.STRIPE;
  addonsNavItems: NavigationItem[] = [];
  private platform: PlatformNameEnum = PlatformNameEnum.SPL;
  @Input() sideBarOpened!: boolean;
  @Input() isOnBoarding!: boolean;
  lang = '';

  constructor(
    private _mediaWatcherService: MediaWatcherService,
    private authFacade: AuthFacade,
    private dashboardService: DashboardService,
    private platformConfigFacade: PlatformConfigFacade,
    private addonsFacade: AddonsFacade,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.lang = this.translate.currentLang;
    this.getCompanySubscription();
    this.userRolesSubscription$ = this.authFacade.selectUserRoles
      .pipe(
        shareObservable(),
        tap((it: UserAccessRolesEnum[]) => (this.userRoles = it)),
        switchMap(() => this.addonsFacade.selectAllAddonsLoaded$),
        filter((loaded) => loaded),
        switchMap(() => this.addonsFacade.selectSerializedCustomerAddons$),
        tap((it: ICustomerAddonSerialized[]) => {
          this.setAddonsNavItems(it);
        }),
        switchMap(() => this.dashboardService.getDashboards()),
        tap((it: GeoDashboard[]) => {
          this.setDashboardNavItems(it);
          this.setNavigationItemsValues(this.isOnBoarding);
        })
      )
      .subscribe();

    this.platformConfigFacade.selectPaymentGateway$
      .pipe(
        tap((it: PaymentGateway) => {
          this.currentPaymentGateway = it;
        })
      )
      .subscribe();

    this.mediaWatcher$ = this._mediaWatcherService.onMediaChange$.subscribe(
      ({ matchingAliases }) => {
        this.isScreenSmall = !matchingAliases.includes('md');
      }
    );
    this.getPlatformName()
      .pipe(
        tap((platformConfig: PlatformConfig) => {
          this.platform = platformConfig.platform;
        })
      )
      .subscribe();
  }

  private getPlatformName(): Observable<PlatformConfig> {
    return this.platformConfigFacade.selectPlatformConfig$;
  }

  setDashboardNavItems(dashboards: GeoDashboard[]) {
    this.dashboardNavItems = dashboards.map((dashboard) => {
      return {
        id: dashboard.id,
        title: this.lang === 'ar' ? dashboard.nameAr : dashboard.name,
        type: NavigationType.item,
        link: `/dashboards/${dashboard.id}`,
        access: ALL_MEMBERS,
        planAccess:
          this.currentPlan === PlanTypeEnum.EXPERT
            ? ALL_PLANS
            : [dashboard.plan],
      } as NavigationItem;
    });
  }

  setAddonsNavItems(it: ICustomerAddonSerialized[]) {
    this.addonsNavItems = it.map((addon: ICustomerAddonSerialized) => {
      return {
        id: addon.addonId,
        title: addon.title,
        type: NavigationType.item,
        link: `/addons/${addon.title.split(' ').join('-').toLowerCase()}`,
        access: ALL_MEMBERS,
        planAccess: ALL_PLANS,
      } as NavigationItem;
    });
  }

  private getCompanySubscription() {
    this.companySubscription$ = this.authFacade.selectCompanySubscription$
      .pipe(
        tap(
          (companySubscription: PlanSubscription) =>
            (this.currentPlan = companySubscription.selectedPlan)
        )
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isOnBoarding']) {
      if (
        changes['isOnBoarding'].currentValue !==
        changes['isOnBoarding'].previousValue
      ) {
        this.setNavigationItemsValues(changes['isOnBoarding'].currentValue);
      }
    }
  }

  private getDefaultNavigationItems(): NavigationItem[] {
    const navItems: NavigationItem[] = [
      {
        id: 'getSeen',
        title: this.translate.instant('DASHBOARD.GET_SEEN'),
        tooltip: this.translate.instant('DASHBOARD.GET_SEEN'),
        icon: 'eye',
        access: ALL_MEMBERS,
        planAccess: ALL_PLANS,
        children: [
          {
            id: 'yourLocations',
            title: this.translate.instant('DASHBOARD.YOUR_LOCATIONS'),
            type: NavigationType.collapsable,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'allLocations',
                title: this.translate.instant('DASHBOARD.ALL_LOCATIONS'),
                type: NavigationType.item,
                link: '/locations/presence-management/all',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'googleUpdateQueues',
                title: this.translate.instant(
                  'DASHBOARD.GOOGLE_UPDATE_QUEUES'
                ),
                type: NavigationType.item,
                link: '/locations/presence-management/google-update-queues',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'blocked',
                title: this.translate.instant(
                  'DASHBOARD.LOCATIONS_REQUIRES_ACTION'
                ),
                type: NavigationType.item,
                link: '/locations/presence-management/blocked',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'allGood',
                title: this.translate.instant('DASHBOARD.LIVE'),
                type: NavigationType.item,
                link: '/locations/presence-management/all-good',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'bulkUpdateJobs',
                title: this.translate.instant('DASHBOARD.BULK_UPDATE_JOBS'),
                type: NavigationType.item,
                link: '/locations/presence-management/location/bulk-update-jobs',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                ],
                planAccess: ALL_PLANS,
              },
            ],
          },
          {
            id: 'yourPages',
            title: this.translate.instant('PAGES.PAGES'),
            type: NavigationType.collapsable,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'facebookPages',
                title: this.translate.instant('PAGES.FACEBOOK'),
                type: NavigationType.item,
                link: '/facebook-pages/presence-management/all',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
            ],
          },
          {
            id: 'socialPosting',
            title: this.translate.instant('DASHBOARD.YOUR_POSTS'),
            tooltip: this.translate.instant('DASHBOARD.YOUR_POSTS_TOOLTIP'),
            type: NavigationType.collapsable,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'allPosted',
                title: this.translate.instant('DASHBOARD.ALL_POSTS'),
                type: NavigationType.item,
                link: 'social-posting/posts-listing',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'published',
                title: this.translate.instant('DASHBOARD.PUBLISHED'),
                type: NavigationType.item,
                link: 'social-posting/posts-listing/published',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'draft',
                title: this.translate.instant('DASHBOARD.DRAFT'),
                type: NavigationType.item,
                link: 'social-posting/posts-listing/draft',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'scheduled',
                title: this.translate.instant('DASHBOARD.SCHEDULED'),
                type: NavigationType.item,
                link: 'social-posting/posts-listing/scheduled',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
            ],
          },
        ],
      },
      {
        id: 'getChosen',
        title: this.translate.instant('DASHBOARD.GET_CHOSEN'),
        tooltip: this.translate.instant('DASHBOARD.GET_CHOSEN'),
        icon: 'get-chosen',
        access: ALL_MEMBERS,
        planAccess: ALL_PLANS,
        children: [
          {
            id: 'manageReviews',
            title: this.translate.instant('DASHBOARD.REVIEW_MANAGEMENT'),
            type: NavigationType.collapsable,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'allReviews',
                title: this.translate.instant('DASHBOARD.ALL_REVIEWS'),
                type: NavigationType.item,
                link: '/reviews/filter/all',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'requireAction',
                title: this.translate.instant('DASHBOARD.REQUIRES_ACTION'),
                type: NavigationType.item,
                link: '/reviews/filter/requires-action',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'treated',
                title: this.translate.instant('DASHBOARD.TREATED'),
                type: NavigationType.item,
                link: '/reviews/filter/treated',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
            ],
          },
          {
            id: 'getReviews',
            title: this.translate.instant('DASHBOARD.GET_REVIEWS'),
            type: NavigationType.collapsable,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'getReviews',
                title: this.translate.instant('DASHBOARD.GET_REVIEWS_BY_EMAIL'),
                type: NavigationType.item,
                link: '/reviews/get-reviews',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
              {
                id: 'qrcode',
                title: this.translate.instant(
                  'DASHBOARD.GET_REVIEWS_BY_QR_CODE'
                ),
                type: NavigationType.item,
                link: '/reviews/get-qrcode',
                access: ALL_MEMBERS,
                planAccess: ALL_PLANS,
              },
            ],
          },
        ],
      },
      {
        id: 'yourGrowth',
        title: this.translate.instant('DASHBOARD.GET_GROWING'),
        tooltip: this.translate.instant('DASHBOARD.GET_GROWING'),
        icon: 'growth',
        access: [
          UserAccessRolesEnum.COMPANY_ADMIN,
          UserAccessRolesEnum.BRAND_ADMIN,
          UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
          UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
          UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
        ],
        planAccess: ALL_PLANS,
        children: [
          {
            id: 'advancedAnalytics',
            title: this.translate.instant('DASHBOARD.ADVANCED_ANALYTICS'),
            type: NavigationType.collapsable,
            link: '/advancedAnalytics',
            access: [
              UserAccessRolesEnum.COMPANY_ADMIN,
              UserAccessRolesEnum.BRAND_ADMIN,
              UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
              UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
              UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
            ],
            planAccess: ALL_PLANS,
            children: this.dashboardNavItems,
          },
          {
            id: 'exportData',
            title: this.translate.instant('DASHBOARD.EXPORT_DATA'),
            type: NavigationType.collapsable,
            access: [
              UserAccessRolesEnum.COMPANY_ADMIN,
              UserAccessRolesEnum.BRAND_ADMIN,
              UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
              UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
              UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
            ],
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'exportSearchViews',
                title: this.translate.instant('DASHBOARD.EXPORT_SEARCH'),
                type: NavigationType.item,
                link: '/export-data/search-views',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                  UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
                  UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
                  UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
                ],
                planAccess: ALL_PLANS,
              },
              {
                id: 'exportKeywords',
                title: this.translate.instant('DASHBOARD.EXPORT_KEYWORDS'),
                type: NavigationType.item,
                link: '/export-data/keywords',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                  UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
                  UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
                  UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
                ],
                planAccess: ALL_PLANS,
              },
              {
                id: 'exportInteractions',
                title: this.translate.instant('DASHBOARD.EXPORT_INTERACTIONS'),
                type: NavigationType.item,
                link: '/export-data/interactions',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                  UserAccessRolesEnum.ACCESS_GROUP_ADMIN,
                  UserAccessRolesEnum.BRAND_SOCIAL_MANAGER,
                  UserAccessRolesEnum.ACCESS_GROUP_SOCIAL_MANAGER,
                ],
                planAccess: ALL_PLANS,
              },
            ],
          },
        ],
      },
      {
        id: 'adminTools',
        title: this.translate.instant('DASHBOARD.ADMIN_TOOLS'),
        tooltip: this.translate.instant('DASHBOARD.ADMIN_TOOLS_TOOLTIP'),
        icon: 'settings',
        access: ALL_MEMBERS,
        planAccess: ALL_PLANS,
        children: [
          {
            id: 'profile',
            title: this.translate.instant('DASHBOARD.PROFILE'),
            type: NavigationType.item,
            link: '/user-profile',
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
          },
          {
            id: 'team',
            title: this.translate.instant('DASHBOARD.TEAM'),
            type: NavigationType.item,
            link: '/team-management/team',
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
          },
          {
            id: 'company',
            title: this.translate.instant('DASHBOARD.COMPANY'),
            type: NavigationType.collapsable,
            access: [
              UserAccessRolesEnum.COMPANY_ADMIN,
              UserAccessRolesEnum.BRAND_ADMIN,
            ],
            planAccess: ALL_PLANS,
            children: [
              {
                id: 'companyInfo',
                title: this.translate.instant('DASHBOARD.COMPANY_INFO'),
                type: NavigationType.item,
                link: '/company-info',
                access: [UserAccessRolesEnum.COMPANY_ADMIN],
                planAccess: ALL_PLANS,
              },
              {
                id: 'brands',
                title: this.translate.instant('DASHBOARD.MY_BRANDS'),
                type: NavigationType.item,
                link: '/brands',
                access: [UserAccessRolesEnum.COMPANY_ADMIN],
                planAccess: ALL_PLANS,
              },
              {
                id: 'responseTemplates',
                title: this.translate.instant('DASHBOARD.RESPONSE_TEMPLATES'),
                type: NavigationType.item,
                link: '/brands/response-templates',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                ],
                planAccess: ALL_PLANS,
              },
              {
                id: 'integrations',
                title: this.translate.instant('DASHBOARD.INTEGRATIONS'),
                type: NavigationType.item,
                link: '/brands/integrations',
                access: [UserAccessRolesEnum.COMPANY_ADMIN],
                planAccess: ALL_PLANS,
              },
              {
                id: 'automaticRules',
                title: this.translate.instant('DASHBOARD.AUTOMATIC_RULE'),
                type: NavigationType.item,
                link: '/brands/rule',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                ],
                planAccess: ALL_PLANS,
              },
              {
                id: 'competitors',
                title: this.translate.instant('DASHBOARD.COMPETITORS'),
                type: NavigationType.item,
                link: '/competitors',
                access: [
                  UserAccessRolesEnum.COMPANY_ADMIN,
                  UserAccessRolesEnum.BRAND_ADMIN,
                ],
                planAccess: [PlanTypeEnum.EXPERT],
              },
              {
                id: 'aiReplySettings',
                title: this.translate.instant('DASHBOARD.AI_REPLY_SETTINGS'),
                type: NavigationType.item,
                link: '/brands/ai-reply-settings',
                access: [UserAccessRolesEnum.COMPANY_ADMIN],
                planAccess: ALL_PLANS,
              },
            ],
          },
          {
            id: 'subscription',
            title: this.translate.instant('DASHBOARD.SUBSCRIPTION'),
            type: NavigationType.item,
            link: '/subscription',
            access: [UserAccessRolesEnum.COMPANY_ADMIN],
            planAccess: ALL_PLANS,
          },
          ...(this.platform !== 'SPL'
            ? [
                {
                  id: 'addons',
                  title: this.translate.instant('COMPANY.ADD-ONS'),
                  type: NavigationType.collapsable,
                  access: [UserAccessRolesEnum.COMPANY_ADMIN],
                  planAccess: ALL_PLANS,
                  children: [
                    {
                      id: 'addons',
                      title: this.translate.instant('COMPANY.ALL_ADD-ONS'),
                      type: NavigationType.item,
                      link: '/addons',
                      access: [UserAccessRolesEnum.COMPANY_ADMIN],
                      planAccess: ALL_PLANS,
                    },
                    ...this.addonsNavItems,
                  ],
                },
              ]
            : []),
          {
            id: 'tags',
            title: this.translate.instant('DASHBOARD.TAGS'),
            type: NavigationType.item,
            link: '/tags',
            access: [UserAccessRolesEnum.COMPANY_ADMIN],
            planAccess: ALL_PLANS,
          },
          {
            id: 'accessGroups',
            title: this.translate.instant('DASHBOARD.ACCESS_GROUPS'),
            type: NavigationType.item,
            link: '/team-management/access-groups',
            access: [UserAccessRolesEnum.COMPANY_ADMIN],
            planAccess: ALL_PLANS,
          },
          {
            id: 'logOut',
            title: this.translate.instant('DASHBOARD.LOG_OUT'),
            type: NavigationType.item,
            access: ALL_MEMBERS,
            planAccess: ALL_PLANS,
            onClick: () => this.signOut(),
          },
        ],
      },
    ] as NavigationItem[];

    const parentNavItems: NavigationItem[] = navItems.filter(
      (navItem: NavigationItem) => {
        let shouldShow: boolean = true;
        shouldShow = this.userRoles.some(
          (element: UserAccessRolesEnum) =>
            navItem.access.includes(element) &&
            navItem.planAccess?.includes(this.currentPlan)
        );

        if (shouldShow && navItem.paymentGatewayAccess?.length) {
          shouldShow = navItem.paymentGatewayAccess?.includes(
            this.currentPaymentGateway
          );
        }

        return shouldShow;
      }
    );

    const all: NavigationItem[] = parentNavItems.map((item: NavigationItem) => {
      return this.getItemsThatBelongToTheUserRole(item);
    });

    return all as NavigationItem[];
  }

  getItemsThatBelongToTheUserRole(item: NavigationItem): NavigationItem {
    if (item.children) {
      const childrenWithAccess: NavigationItem[] = item.children.filter(
        (child: NavigationItem) => {
          let shouldShow = this.userRoles.some(
            (element: UserAccessRolesEnum) =>
              child.access.includes(element) &&
              child.planAccess?.includes(this.currentPlan)
          );

          if (shouldShow && child.paymentGatewayAccess?.length) {
            shouldShow = child.paymentGatewayAccess?.includes(
              this.currentPaymentGateway
            );
          }

          return shouldShow;
        }
      );

      const children: NavigationItem[] = childrenWithAccess.map(
        (item: NavigationItem) => {
          return this.getItemsThatBelongToTheUserRole(item);
        }
      );
      return { ...item, children: children } as NavigationItem;
    }
    return item;
  }

  private getOnboardingNavigationItems(): NavigationItem[] {
    return [
      {
        id: 'logOut',
        title: this.translate.instant('DASHBOARD.LOG_OUT'),
        tooltip: this.translate.instant('DASHBOARD.LOG_OUT_TOOLTIP'),
        icon: 'logout',
        onClick: () => this.signOut(),
      },
    ] as NavigationItem[];
  }

  ngOnDestroy(): void {
    this.mediaWatcher$.unsubscribe();
    this.userRolesSubscription$.unsubscribe();
    this.companySubscription$.unsubscribe();
  }

  private signOut(): void {
    localStorage.removeItem('reviewsCount');
    this.authFacade.signOut();
  }

  private setNavigationItemsValues(isOnBoarding: boolean) {
    if (isOnBoarding) {
      this.navigationItems = this.getOnboardingNavigationItems();
    } else {
      this.navigationItems = this.getDefaultNavigationItems();
    }
  }
}
